// ─── src/components/sections/VenueGrid.jsx ──────────────────────────────────
import { useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { track } from "../../utils/track";
import LuxuryVenueCard from "../cards/LuxuryVenueCard";
import SliderNav from "../ui/SliderNav";
import QuickViewModal from "../modals/QuickViewModal";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setMobile(e.matches);
    mql.addEventListener("change", fn);
    return () => mql.removeEventListener("change", fn);
  }, [bp]);
  return mobile;
}

export default function VenueGrid({ venues = [], onViewVenue, onViewCategory }) {
  const C = useTheme();
  const isMobile = useIsMobile();
  const [quickViewItem, setQuickViewItem] = useState(null);
  const display = venues.slice(0, 6);

  // Mobile: vertical snap feed. Desktop: horizontal slider with wider cards.
  if (isMobile) {
    return (
      <>
        <div
          aria-label="Featured wedding venues – Mobile feed"
          className="home-venue-grid-mobile"
          style={{
            position: "relative",
            background: C.black,
            width: "100vw",
            overflowX: "hidden",
            overflowY: "auto",
            scrollSnapType: "y mandatory",
            display: "flex",
            flexDirection: "column",
            margin: 0,
            padding: 0,
            gap: "3px",
          }}
        >
          {display.map((v, idx) => (
            <div
              key={v.id}
              className="home-venue-card"
              style={{
                width: "100vw",
                flex: "0 0 calc(100dvh - 10px)",
                scrollSnapAlign: "start",
                scrollMarginTop: 0,
                borderRadius: 0,
                margin: 0,
                padding: 0,
              }}
            >
              <LuxuryVenueCard
                v={v}
                isMobile={true}
                onView={() => {
                  track("venue_card_click", { id: v.id, name: v.name });
                  onViewVenue?.(v);
                }}
                quickViewItem={quickViewItem}
                setQuickViewItem={setQuickViewItem}
                imgWidth={720}
                priority={idx === 0}
              />
            </div>
          ))}
        </div>
        {/* Quick View modal - outside overflow clip */}
        {quickViewItem && (
          <QuickViewModal
            item={quickViewItem}
            onClose={() => setQuickViewItem(null)}
            onViewFull={() => {
              const raw = display.find((f) => f.id === quickViewItem.id);
              setQuickViewItem(null);
              onViewVenue?.(raw ?? quickViewItem);
            }}
          />
        )}
      </>
    );
  }

  // Desktop: Section with heading + carousel + CTA
  return (
    <>
      <section
        aria-label="Featured wedding venues"
        className="home-venue-grid-section"
        style={{
          position: "relative",
          background: C.black,
          padding: "80px 60px 100px",
          overflow: "hidden",
        }}
      >
        {/* Watermark */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 80,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: "var(--font-heading-primary)",
            fontSize: "clamp(120px, 12vw, 180px)",
            fontWeight: 400,
            fontStyle: "italic",
            color: "rgba(211,154,33,0.06)",
            whiteSpace: "normal",
            width: "100%",
            textAlign: "center",
            pointerEvents: "none",
            userSelect: "none",
            letterSpacing: "-2px",
            lineHeight: 1,
            zIndex: 0,
          }}
        >
          The Collection
        </span>
        <div style={{ maxWidth: 1320, margin: "0 auto", position: "relative", zIndex: 1 }}>
          {/* Heading, mirrors VendorPreview style */}
          <div style={{ marginBottom: 48 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 16,
              }}
            >
              <div
                style={{ width: 28, height: 1, background: "rgba(211,154,33,0.5)" }}
              />
              <span
                style={{
                  fontFamily: NU,
                  fontSize: 10,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: C.gold,
                  fontWeight: 600,
                }}
              >
                The LWD Edit
              </span>
            </div>
            <h2
              style={{
                fontFamily: GD,
                fontSize: "clamp(32px, 3.5vw, 52px)",
                color: C.off,
                fontWeight: 400,
                lineHeight: 1.1,
              }}
            >
              Wedding Venue{" "}
              <br />
              <span style={{ fontStyle: "italic", color: C.gold }}>
                Collective
              </span>
            </h2>
            <p
              style={{
                fontFamily: NU,
                fontSize: 14,
                color: C.grey,
                lineHeight: 1.7,
                maxWidth: 560,
                marginTop: 14,
                fontWeight: 300,
              }}
            >
              Explore our hand-picked selection of the world's most extraordinary
              wedding venues, filtered by location, capacity, style, and features.
            </p>
          </div>

          {/* Horizontal card slider, standardized 360px cards */}
          <div style={{ marginBottom: 48 }}>
            <SliderNav className="home-venue-grid" cardWidth={360} gap={24}>
              {display.map((v, idx) => (
                <div
                  key={v.id}
                  className="home-venue-card"
                  style={{ flex: "0 0 360px", scrollSnapAlign: "start" }}
                >
                  <LuxuryVenueCard
                    v={v}
                    isMobile={false}
                    onView={() => {
                      track("venue_card_click", { id: v.id, name: v.name });
                      onViewVenue?.(v);
                    }}
                    quickViewItem={quickViewItem}
                    setQuickViewItem={setQuickViewItem}
                    imgWidth={720}
                    priority={idx === 0}
                  />
                </div>
              ))}
            </SliderNav>
          </div>

          {/* View all CTA */}
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => onViewCategory?.({ category: "wedding-venues" })}
              style={{
                background: "transparent",
                color: C.gold,
                border: "1px solid rgba(211,154,33,0.4)",
                borderRadius: "var(--lwd-radius-input)",
                padding: "14px 40px",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: NU,
                transition: "all 0.25s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.gold;
                e.currentTarget.style.color = "#0a0906";
                e.currentTarget.style.borderColor = C.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = C.gold;
                e.currentTarget.style.borderColor = "rgba(211,154,33,0.4)";
              }}
            >
              View All Venues →
            </button>
          </div>
        </div>
      </section>

      {/* Quick View modal - outside overflow clip */}
      {quickViewItem && (
        <QuickViewModal
          item={quickViewItem}
          onClose={() => setQuickViewItem(null)}
          onViewFull={() => {
            const raw = display.find((f) => f.id === quickViewItem.id);
            setQuickViewItem(null);
            onViewVenue?.(raw ?? quickViewItem);
          }}
        />
      )}
    </>
  );
}
